import { PivotItem } from '@fluentui/react';
import {
  debounce,
  defaultPalette,
  getDescriptionForItemCount,
  ISearchProps,
  Pivot,
  pivotContainerStylesFill,
  pivotStylesFill,
  Search,
  useClassNames,
} from '@h2oai/ui-kit';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Feature } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle, DetailField, featureSetFlowNameMap } from '../../featurestore/models';
import { ListableFeatureSetWithOwners } from '../../pages/Featurestore/FeatureSetDetails/FeatureSetDetails';
import { useSearchParams } from '../../utils/hooks';
import { formatDefaultDateAndTime } from '../../utils/utils';
import { DetailHeader, getOwnerNames } from '../DetailHeader/DetailHeader';
import { FeatureList } from '../FeatureList/FeatureList';
import { FeatureSetMetaDataFields } from '../FeatureSetMetaDataFields/FeatureSetMetaDataFields';
import {
  featureSetMetaDataStylesDefault,
  IFeatureSetMetaDataClassNames,
  IFeatureSetMetaDataStyles,
} from './FeatureSetMetaData.styles';

const getFields = (data: ListableFeatureSetWithOwners): DetailField[][] => [
  [
    { label: 'Feature set type', value: data.featureSetType, color: defaultPalette.blue100 },
    { label: 'Primary key', value: data.primaryKey ? data.primaryKey.join('|') : '' },
    { label: 'Time Travel Column', value: data.timeTravelColumn ? data.timeTravelColumn : 'Auto Generated' },
    { label: 'Partition by', value: data.partitionBy ? data.partitionBy.join('|') : '', color: defaultPalette.red100 },
    { label: 'Latest Version', value: data.version },
    { label: 'Ingested Records', value: data.recordsCount },
    { label: 'Author', value: data.author?.name },
    { label: 'Owners', value: getOwnerNames(data.owners) },
    { label: 'Flow', value: data.flow ? featureSetFlowNameMap.get(data.flow) : '-' },
    { label: 'Derived', value: !!data.derivedFrom ? 'True' : 'False' },
  ],
  [
    { label: 'Latest Update', value: formatDefaultDateAndTime(data.lastUpdateDateTime) },
    { label: 'Created', value: formatDefaultDateAndTime(data.createdDateTime) },
  ],
];

interface IFeatureSetMetaDataProps {
  title?: ContentsTitle;
  reviewId?: string;
  data: ListableFeatureSetWithOwners;
  onOpenFeaturePage: (data: Feature) => void;
}

export const FeatureSetMetaData = ({ reviewId, title, data, onOpenFeaturePage }: IFeatureSetMetaDataProps) => {
  const classNames = useClassNames<IFeatureSetMetaDataStyles, IFeatureSetMetaDataClassNames>(
    'FeatureSetMetaData',
    featureSetMetaDataStylesDefault
  );
  const { featureName } = useParams<{ featureName: string }>();
  const [params, setParams] = useSearchParams();
  const tab = params.get('metadatatab');
  const [searchText, setSearchText] = useState(featureName || '');
  const [collapsedHeader, setCollapsedHeader] = useState(false);
  const [description, setDescription] = useState('0 Features');
  const [selectedPivotKey, setSelectedPivotKey] = useState(tab ? tab : ContentsTitle.features);
  const changeSearchText = useCallback((text: string) => {
    setSearchText(text);
  }, []);
  const onUpdateData = useCallback((count: number, pageSize: number, pageToken: string, name: ContentsTitle) => {
    setDescription(getDescriptionForItemCount(count, pageSize, pageToken, name));
  }, []);
  const onSearchTextChange = useMemo(() => debounce(300, changeSearchText), [changeSearchText]);
  const refRoot = useRef<HTMLDivElement>(null);
  const searchProps: ISearchProps = {
    placeholder: 'Search Features by name',
    defaultSearchText: featureName,
    onSearchTextChange,
  };

  return (
    <div className={classNames.root} ref={refRoot} data-test="feature-set-meta-data">
      <DetailHeader data={data} fields={getFields(data)} collapsed={collapsedHeader} />
      <div className={classNames.pivotContainer}>
        <Pivot
          styles={pivotStylesFill}
          pivotContainerStyles={pivotContainerStylesFill}
          selectedKey={selectedPivotKey}
          data-test="features-keydetails-pivot"
          onLinkClick={(item?: PivotItem) => {
            const title = item?.props.itemKey as ContentsTitle;
            if (title) {
              setSelectedPivotKey(title);
              params.set('metadatatab', title);
              setParams(params);
            }
          }}
          items={[
            {
              key: ContentsTitle.features,
              headerText: ContentsTitle.features,
              content: (
                <div className={classNames.list}>
                  <div className={classNames.description}>
                    <div className={classNames.descriptionText}>{description}</div>
                    <div className={classNames.search}>
                      <Search {...searchProps} />
                    </div>
                  </div>
                  <FeatureList
                    title={title}
                    reviewId={reviewId}
                    data={data}
                    onOpenFeaturePage={onOpenFeaturePage}
                    onUpdateData={onUpdateData}
                    searchText={searchText}
                    onScroll={(e) => setCollapsedHeader(e.currentTarget.scrollTop !== 0)}
                  />
                </div>
              ),
            },
            {
              key: ContentsTitle.keyDetails,
              headerText: ContentsTitle.keyDetails,
              content: (
                <div className={classNames.list}>
                  <FeatureSetMetaDataFields data={data} />
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};
